import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Typography, Spin, Empty, WarningModal, DragSelectedItem } from '../../../../../components';
import { DiscoveryInventoryContainer } from '../../../Inventory/components/DiscoveryInventoryContainer';
import { hasValidIP } from '../../../helpers';
import { MARK_READY_WARNING } from '../../../../../constants/discovery';
import { BaseDiscoveryTable } from '../../../shared';
import { createTableColumns } from '../../../Inventory/components/DiscoveryInventoryTable/tableColumns';
import { DraggableColumnHeader } from '../../../Inventory/components/DiscoveryInventoryTable/components/DraggableColumnHeader';
import { useBaseDiscoveryTable } from '../../../shared/hooks/useBaseDiscoveryTable';
import { MoveGroupsAndAppsTopBar } from '../MoveGroupsAndAppsTopBar/MoveGroupsAndAppsTopBar';
import { defaultSelectedColumns } from '../../../Inventory/components/InventoryTopBar/constants';
import { TableHeader } from './TableHeader';
import { useDraggable, DragOverlay } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

import './MoveGroupsAndAppsTable.scss';

export const MoveGroupsAndAppsTable = ({
    selectedGroup,
    selectedApp,
    selectedInventory,
    selectedUnassigned,
    moveGroups,
    apps,
    onOpenManageModal,
    onSelectionChange,
    onUnsortedCountChange,
    onUnassignedCountChange,
    onChangeStatus,
    moveGroupData,
    applicationData,
    moveGroupLoading,
    moveGroupFetching,
    applicationLoading,
    applicationFetching,
    pagination,
    handlePaginationChange,
    refetchUnassignedInventoryRef,
}) => {
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);
    const [searchValue, setSearchValue] = useState('');
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [draggedRow, setDraggedRow] = useState(null);

    // Determine what is selected and get the name
    let selectedName = 'All';
    let showAllInventory = selectedInventory && !selectedUnassigned;
    let showUnassignedInventory = selectedUnassigned;

    if (selectedGroup) {
        const group = moveGroups.find((g) => g.id === selectedGroup);
        selectedName = group ? group.name : 'Move Group';
        showAllInventory = false;
        showUnassignedInventory = false;
    } else if (selectedApp) {
        const app = apps.find((a) => a.id === selectedApp);
        selectedName = app ? app.name : 'Application';
        showAllInventory = false;
        showUnassignedInventory = false;
    } else if (selectedUnassigned) {
        selectedName = 'Unassigned Inventory';
    }

    // Get current table data based on selection - extract from vms array
    const currentTableData = useMemo(() => {
        if (selectedGroup && moveGroupData) {
            return moveGroupData.vms || [];
        }
        if (selectedApp && applicationData) {
            return applicationData.vms || [];
        }
        return [];
    }, [selectedGroup, selectedApp, moveGroupData, applicationData]);

    const isLoading = selectedGroup ? moveGroupLoading : selectedApp ? applicationLoading : false;
    const isFetching = selectedGroup ? moveGroupFetching : selectedApp ? applicationFetching : false;

    // For filtered views (move group or application), use the base table hook
    const columns = createTableColumns({ handleChangeStatus: () => {} });
    const {
        rowSelection,
        visibleColumns,
        columnOrder,
        handleDragEnd,
        selectedRowKeys: filteredSelectedRowKeys,
    } = useBaseDiscoveryTable({
        tableData: currentTableData,
        columns,
        selectedColumns: defaultSelectedColumns,
        tableType: 'move_groups_applications_table',
    });

    // Get selected rows from keys and table data
    const selectedRows = useMemo(() => {
        return currentTableData.filter((row) => filteredSelectedRowKeys.includes(row.id));
    }, [currentTableData, filteredSelectedRowKeys]);

    // Custom row wrapper to make rows draggable
    const DraggableRow = ({ record, children, ...props }) => {
        if (!record) {
            return <tr {...props}>{children}</tr>;
        }

        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: `table-row-${record.id}`,
            data: {
                type: 'vm',
                sourceId: record.id,
                sourceName: record.name,
                sourceIds:
                    filteredSelectedRowKeys.includes(record.id) && filteredSelectedRowKeys.length > 1
                        ? filteredSelectedRowKeys
                        : [record.id],
                record,
            },
        });

        // Track dragged row for overlay
        useEffect(() => {
            if (isDragging) {
                setDraggedRow(record);
            } else if (draggedRow?.id === record.id) {
                setDraggedRow(null);
            }
        }, [isDragging]);

        return (
            <tr
                ref={setNodeRef}
                {...props}
                {...attributes}
                {...listeners}
                style={{
                    ...props.style,
                    cursor: isDragging ? 'grabbing' : 'grab',
                }}
            >
                {children}
            </tr>
        );
    };

    // Sync selection to parent
    const handleSelectionChange = useCallback(
        (keys) => {
            onSelectionChange?.(keys);
        },
        [onSelectionChange],
    );

    // Handle changing ready status with IP validation
    const handleChangeReadyStatus = useCallback(
        (vmIds, status) => {
            if (status === 'ready' && selectedRows.some((row) => !hasValidIP(row.ip_addresses))) {
                setShowWarningModal(true);
                return;
            }
            onChangeStatus?.(vmIds, status);
        },
        [onChangeStatus, selectedRows],
    );

    // Only sync filteredSelectedRowKeys when showing filtered views (not all/unassigned inventory)
    useEffect(() => {
        if (!showAllInventory && !showUnassignedInventory) {
            onSelectionChange?.(filteredSelectedRowKeys);
        }
    }, [filteredSelectedRowKeys, onSelectionChange, showAllInventory, showUnassignedInventory]);

    // Handle all inventory view
    if (showAllInventory) {
        return (
            <div className="move-groups-apps-inventory-container">
                <TableHeader selectedName={selectedName} />
                <DiscoveryInventoryContainer
                    key="all-inventory"
                    moveGroupsAndApps={true}
                    onOpenManageModal={onOpenManageModal}
                    onSelectionChange={handleSelectionChange}
                    onTotalElementsChange={onUnsortedCountChange}
                />
            </div>
        );
    }

    // Handle unassigned inventory view
    if (showUnassignedInventory) {
        return (
            <div className="move-groups-apps-inventory-container">
                <TableHeader selectedName={selectedName} />
                <DiscoveryInventoryContainer
                    key="unassigned-inventory"
                    moveGroupsAndApps={true}
                    onOpenManageModal={onOpenManageModal}
                    onSelectionChange={handleSelectionChange}
                    onTotalElementsChange={onUnassignedCountChange}
                    unassignedOnly={true}
                    refetchUnassignedInventoryRef={refetchUnassignedInventoryRef}
                />
            </div>
        );
    }

    // Handle empty state for move group or application
    if ((selectedGroup || selectedApp) && !isLoading && currentTableData.length === 0) {
        return (
            <div className="move-groups-apps-inventory-container">
                <TableHeader selectedName={selectedName} moveGroupData={selectedGroup ? moveGroupData : undefined} />
                <Empty
                    description={`No VMs assigned to this ${selectedGroup ? 'move group' : 'application'} yet.`}
                    className="empty-state-container"
                />
            </div>
        );
    }

    // Handle loading state
    if (isLoading) {
        return (
            <div className="move-groups-apps-inventory-container">
                <TableHeader selectedName={selectedName} moveGroupData={selectedGroup ? moveGroupData : undefined} />
                <div className="loading-state-container">
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    // Render table for move group or application view
    return (
        <div className="move-groups-apps-inventory-container">
            <TableHeader selectedName={selectedName} moveGroupData={selectedGroup ? moveGroupData : undefined} />
            <MoveGroupsAndAppsTopBar
                handleColumnSelection={() => {}}
                selectedColumns={defaultSelectedColumns}
                selectedRowKeys={filteredSelectedRowKeys}
                handleFilterDiscoveryInventoryData={() => {}}
                resetFilters={() => {}}
                refetchInventoryData={() => {}}
                setSearchValue={setSearchValue}
                searchValue={searchValue}
                selectedFilters={[]}
                setSelectedFilters={() => {}}
                handleChangeStatus={handleChangeReadyStatus}
                onOpenManageModal={onOpenManageModal}
            />
            <BaseDiscoveryTable
                tableData={currentTableData}
                isLoading={isLoading}
                isFetching={isFetching}
                pagination={pagination}
                handlePaginationChange={handlePaginationChange}
                rowSelection={rowSelection}
                visibleColumns={visibleColumns}
                columnOrder={columnOrder}
                handleDragEnd={handleDragEnd}
                DraggableColumnHeader={DraggableColumnHeader}
                disableSorting={true}
                enableRowDragging={true}
                customComponents={{
                    body: {
                        row: (props) => {
                            const rowKey = props['data-row-key'];
                            const record = rowKey ? currentTableData.find((r) => r.id === rowKey) : null;
                            return <DraggableRow {...props} record={record} />;
                        },
                    },
                }}
            />
            <WarningModal open={showWarningModal} onOk={() => setShowWarningModal(false)}>
                {MARK_READY_WARNING}
            </WarningModal>
            <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
                {draggedRow ? (
                    <DragSelectedItem
                        label={draggedRow.name || 'VM'}
                        count={
                            filteredSelectedRowKeys.includes(draggedRow.id) && filteredSelectedRowKeys.length > 1
                                ? filteredSelectedRowKeys.length
                                : 1
                        }
                    />
                ) : null}
            </DragOverlay>
        </div>
    );
};
